// Live channel state for a light (RGB / WWH / WWRGB), shared by both UIs.
//
// Same shape as useRelayStatus: seed from the server's last status, then follow
// `<uno>/client`. Unlike the relays, a light's UI DOES move under the finger —
// dragging a slider or the colour wheel updates locally straight away, because a
// colour picker that only moved when the device replied would be unusable.
// Publishing is what's rate-limited instead.
//
// `applying` drives the "Applying Changes..." veil: Flutter raises it on every
// publish and drops it after a couple of seconds, so a user doesn't tap twice
// while the fixture catches up.
//
// REVERSED white / warm (core/devices/lightReverse): `values` are always what the
// user sees; channels 0 and 1 are exchanged only on the wire, both ways.
//
// ⚠️ AND NOT EVEN ON THE WIRE WHERE THE FIRMWARE DOES IT. Newer firmware holds
// the flag itself (EEPROM / NVS), so the exchange here would cancel the
// device's. `reversed` is the fact about the fixture — it is what the menu
// label and the caption read — while `swapOnWire` is this app's share of the
// work, and only the second one touches a payload.
//
// On / Off / Night / Blink match the BLE lamp block (useBleNode), over MQTT:
//   On     the last colour that was lit here, else full white
//   Night  a low warm glow (light.NIGHT_VALUES)
//   Blink  off/on ×5 (1 s off, 0.7 s on), then back to what it showed before.
//          The Wi-Fi firmware has no blink word, so the app times it, and the
//          status echoes of its own flashes are ignored while it runs.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../mqtt/mqttClient';
import { fetchSeedStatus } from '../devices/deviceStatus';
import { publishCommand } from '../utils/deviceController';
import {
  FULL_WHITE, NIGHT_VALUES, formatChannels, parseChannels, typeKeyForCount,
} from '../devices/light';
import {
  appMustSwap, cachedChipSwaps, cachedReversed, chipSwapsWhite, fromWire,
  getLightReversed, hasWhitePair, onReverseChange, toWire,
} from '../devices/lightReverse';

export const BLINK_COUNT = 5;
export const BLINK_OFF_MS = 1000;
export const BLINK_ON_MS = 700;

const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });

export function useLightChannels(uniqueNumber, count, userId, applyMs = 2000) {
  const [values, setValuesState] = useState(() => new Array(count).fill(0));
  const [applying, setApplying] = useState(false);
  const [blinking, setBlinking] = useState(false);
  const [reversed, setReversed] = useState(() => !!cachedReversed(uniqueNumber));
  // What the app itself must exchange — see the note at the top of the file.
  const [swapOnWire, setSwapOnWire] = useState(() => appMustSwap(uniqueNumber));
  const applyTimer = useRef(null);
  const debounce = useRef(null);
  const valuesRef = useRef(values);
  const lastLit = useRef(null);       // the last non-dark colour seen, for On
  const blinkRef = useRef(false);     // ignore status echoes while blinking
  const alive = useRef(true);
  // Status arrives in WIRE order; keep the raw one so a reverse flip re-reads it.
  const lastWire = useRef(null);
  // ⚠️ THE REF CARRIES THE WIRE FLAG, NOT THE FIXTURE'S. Every publish and every
  // status echo below reads it, and they are the only places the exchange may
  // happen.
  const reversedRef = useRef(swapOnWire);
  reversedRef.current = swapOnWire;

  const setValues = useCallback((next) => {
    setValuesState((prev) => {
      const v = typeof next === 'function' ? next(prev) : next;
      valuesRef.current = v;
      if (v.some((n) => n > 0)) lastLit.current = v;
      return v;
    });
  }, []);

  const applyWire = useCallback((wire) => {
    lastWire.current = wire;
    setValues(fromWire(wire, reversedRef.current));
  }, [setValues]);

  useEffect(() => () => { alive.current = false; }, []);

  // The reverse flag — only a white pair can be reversed.
  useEffect(() => {
    if (!uniqueNumber || !hasWhitePair(count)) {
      setReversed(false);
      setSwapOnWire(false);
      return undefined;
    }
    let on = true;
    const settle = () => {
      if (!on) return;
      setReversed(!!cachedReversed(uniqueNumber));
      setSwapOnWire(appMustSwap(uniqueNumber));
    };
    getLightReversed({ UniqueNumber: uniqueNumber }).then((r) => {
      if (!on) return;
      settle();
      // ⚠️ ASKED ONLY WHEN THE QUESTION ARISES, because asking every white light
      // on every room page would be a probe per card. It arises when the
      // fixture is reversed, and also when this phone has ever seen THIS device
      // swapping for itself — that is the mirror case: a chip still holding the
      // flag after the column has gone back to normal, which the app has to
      // compensate for until the word gets through (the XOR in lightReverse).
      if (r || cachedChipSwaps(uniqueNumber) === 1) chipSwapsWhite(uniqueNumber).then(settle);
    });
    const off = onReverseChange((uno) => { if (String(uno) === String(uniqueNumber)) settle(); });
    return () => { on = false; off(); };
  }, [uniqueNumber, count]);

  // A flip re-reads the last status the right way round.
  useEffect(() => {
    if (lastWire.current) setValues(fromWire(lastWire.current, swapOnWire));
  }, [swapOnWire, setValues]);

  // Seed from the last recorded status.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    let on = true;
    fetchSeedStatus(uniqueNumber).then((raw) => {
      const parsed = parseChannels(raw, count);
      if (on && parsed && !blinkRef.current) applyWire(parsed);
    });
    return () => { on = false; };
  }, [uniqueNumber, count, applyWire]);

  // Follow live status.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    const topic = `${uniqueNumber}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      const open = payload.indexOf('[');
      const close = payload.indexOf(']');
      if (open === -1 || close === -1) return;
      if (payload.substring(open + 1, close) !== String(uniqueNumber)) return;
      const s = `${payload}#`;
      const body = s.substring(s.indexOf(']') + 1, s.indexOf('#'));
      const parsed = parseChannels(body, count);
      if (parsed && !blinkRef.current) applyWire(parsed);
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uniqueNumber, count, applyWire]);

  useEffect(() => () => {
    if (applyTimer.current) clearTimeout(applyTimer.current);
    if (debounce.current) clearTimeout(debounce.current);
  }, []);

  const raiseApplying = useCallback(() => {
    setApplying(true);
    if (applyTimer.current) clearTimeout(applyTimer.current);
    applyTimer.current = setTimeout(() => setApplying(false), applyMs);
  }, [applyMs]);

  // The one place a channel list leaves the app.
  const publishValues = useCallback((v) => {
    publishCommand(uniqueNumber, formatChannels(toWire(v, reversedRef.current)), userId);
  }, [uniqueNumber, userId]);

  // Publish the current channel values.
  const commit = useCallback((next) => {
    publishValues(next ?? valuesRef.current);
    raiseApplying();
  }, [publishValues, raiseApplying]);

  // Publish a fixed payload (the preset buttons) — in the user's channel order.
  const sendCommand = useCallback((command) => {
    const parsed = parseChannels(command, count);
    if (!parsed) return;
    setValues(parsed);
    publishValues(parsed);
    raiseApplying();
  }, [count, setValues, publishValues, raiseApplying]);

  const sendValues = useCallback((v) => {
    setValues(v);
    publishValues(v);
    raiseApplying();
  }, [setValues, publishValues, raiseApplying]);

  // Move the UI now, publish once the user pauses — the colour wheel would
  // otherwise emit a frame per pointer move.
  const setAndDebounce = useCallback((next, ms = 300) => {
    setValues(next);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => commit(next), ms);
  }, [commit, setValues]);

  const key = typeKeyForCount(count);
  const turnOn = useCallback(() => sendValues(lastLit.current || FULL_WHITE[key]), [sendValues, key]);
  const turnOff = useCallback(() => sendValues(new Array(count).fill(0)), [sendValues, count]);
  const nightMode = useCallback(() => sendValues(NIGHT_VALUES[key]), [sendValues, key]);

  const blink = useCallback(async () => {
    if (blinkRef.current || !uniqueNumber) return;
    const before = valuesRef.current;
    const lit = before.some((n) => n > 0) ? before : (lastLit.current || FULL_WHITE[key]);
    const dark = new Array(count).fill(0);
    blinkRef.current = true;
    setBlinking(true);
    try {
      for (let i = 0; i < BLINK_COUNT && alive.current; i += 1) {
        publishValues(dark);
        await wait(BLINK_OFF_MS); // eslint-disable-line no-await-in-loop
        publishValues(lit);
        await wait(BLINK_ON_MS); // eslint-disable-line no-await-in-loop
      }
      publishValues(before);
    } finally {
      blinkRef.current = false;
      if (alive.current) {
        setValues(before);
        setBlinking(false);
      }
    }
  }, [uniqueNumber, key, count, publishValues, setValues]);

  return {
    values,
    setValues,
    applying,
    commit,
    sendCommand,
    setAndDebounce,
    turnOn,
    turnOff,
    nightMode,
    blink,
    blinking,
    reversed,
    // Who is doing it, for the menu's second line: true once the device has
    // answered that it holds the flag itself.
    chipSwaps: cachedChipSwaps(uniqueNumber) === 1,
    canReverse: hasWhitePair(count),
  };
}
